import {VoyadoEngageCart, VoyadoEngageProduct,} from "./models/voyadoEngageWebTracking.models";

declare const va: undefined | ((command: string, payload?: any) => void);

export class VoyadoEngageWebTrackingService {
    private static isDev = import.meta.env.MODE === "development";
    private static defaultLocale = "nb-NO";

    private static log(...args: any[]) {
        if (this.isDev) {
            console.log("[VoyadoCartTrackingService]", ...args);
        }
    }

    /** Helper: returnerer true hvis Voyado-scriptet er lastet inn */
    private static isVoyadoAvailable(): boolean {
        return typeof va === "function";
    }

    /**
     * @description Used to identify the user. Should be invoked as soon as a visitor is identified by the site.
     * Keep in mind the visitor might already be identified (such as by an email link click), and invoking this method incorrectly might clear the existing identification.
     * @param contactId - The contactId of the user
     * @returns
     */
    static setContactId(contactId: string) {
        if (!this.isVoyadoAvailable()) return;

        const contactIdCookieValue = document.cookie
            .split("; ")
            .find((row) => row.startsWith("_vaI="))
            ?.split("=")[1];

        if (contactIdCookieValue === contactId) {
            this.log("setContactId", contactId, "contact already identified");
            return;
        }

        this.log("setContactId", contactId);
        va!("setContactId", contactId);
    }

    /**
     * Track cart event.
     * @description Used for registering visitor actions related to any cart modification
     * @param cart - The cart object to track.
     */
    static trackCart(cart: VoyadoEngageCart) {
        if (!this.isVoyadoAvailable()) return;
        this.log("trackCart", cart);
        va!("cart", cart);
    }

    /**
     * Track cart event.
     * @description Used for pruning the cart of all items, usually when indicating a purchase or logout by the e-com visitor
     * @param cartRef - The cart reference to track.
     */
    static emptyCart(cartRef: string) {
        if (!this.isVoyadoAvailable()) return;
        this.log("emptyCart", cartRef);
        va!("emptyCart", {
            cartRef,
        });
    }

    /**
     * Track product view event.
     * @description Used for tracking product views. Invoke this method every time a product is viewed, such as on a product page, whether the user has been identified by the website or not.
     * @param product - The product object to track.
     */
    static trackProductView(product: VoyadoEngageProduct) {
        if (!this.isVoyadoAvailable()) return;
        const payload = {
            locale: this.defaultLocale,
            ...product,
        };
        this.log("productview", payload);
        va!("productview", payload);
    }
}
